import { Injectable, inject, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { MessageService } from "primeng/api";
import { environment } from "../../environments/environment";
import { interval, switchMap, catchError, of, Subscription, Subject, timer } from "rxjs";
import { webSocket, WebSocketSubject } from "rxjs/webSocket";

export interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  link: string;
  is_read: boolean;
  created_at: string;
}

export interface NotificationPage {
  count: number;
  results: Notification[];
}

export interface NotificationPreference {
  id: number;
  channel: string;
  enabled: boolean;
}

interface WsMessage {
  type: string;
  notification: Notification;
  unread_count: number;
}

const NOTIFICATION_SEVERITY: Record<string, string> = {
  guess_validated: "success",
  guess_rejected: "error",
  guess_uncertain: "warn",
  observation_assigned: "info",
  contributor_added: "info",
  event_invited: "info",
  event_revoked: "warn",
};

@Injectable({ providedIn: "root" })
export class NotificationService {
  private http = inject(HttpClient);
  private messageService = inject(MessageService);
  private apiUrl = `${environment.apiUrl}/monitoring/notifications`;
  private prefUrl = `${environment.apiUrl}/monitoring/notification-preferences`;

  unreadCount = signal(0);

  private ws$?: WebSocketSubject<WsMessage>;
  private wsSub?: Subscription;
  private pollSub?: Subscription;
  private reconnectSub?: Subscription;
  private reconnectAttempts = 0;
  private readonly maxReconnectDelay = 30000;
  private readonly fallbackPollInterval = 60000;
  private token?: string;

  /** New notification event — components can subscribe for side-effects. */
  readonly newNotification$ = new Subject<Notification>();

  connect(token: string) {
    this.token = token;
    this.stopAll();
    this.fetchUnreadCount();
    this.openWebSocket(token);
  }

  disconnect() {
    this.token = undefined;
    this.stopAll();
    this.unreadCount.set(0);
  }

  private openWebSocket(token: string) {
    if (!environment.wsUrl) {
      this.startFallbackPolling();
      return;
    }

    const url = `${environment.wsUrl}/notifications/?token=${token}`;
    this.ws$ = webSocket<WsMessage>({
      url,
      openObserver: {
        next: () => {
          this.reconnectAttempts = 0;
          this.stopPolling();
        },
      },
      closeObserver: {
        next: () => this.scheduleReconnect(),
      },
    });

    this.wsSub = this.ws$.subscribe({
      next: (msg) => this.handleWsMessage(msg),
      error: () => this.scheduleReconnect(),
    });
  }

  private handleWsMessage(msg: WsMessage) {
    if (msg.type === "notification.new") {
      this.unreadCount.set(msg.unread_count);
      this.newNotification$.next(msg.notification);
      this.messageService.add({
        severity: NOTIFICATION_SEVERITY[msg.notification.type] || "info",
        summary: msg.notification.title,
        detail: msg.notification.message,
        life: 7000,
      });
    }
  }

  private scheduleReconnect() {
    if (!this.token) return;

    const delay = Math.min(
      1000 * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay,
    );
    this.reconnectAttempts++;

    // Fall back to polling while waiting
    this.startFallbackPolling();

    this.reconnectSub?.unsubscribe();
    this.reconnectSub = timer(delay).subscribe(() => {
      if (this.token) {
        this.stopPolling();
        this.openWebSocket(this.token);
      }
    });
  }

  // ── HTTP polling fallback ──────────────────────────────────────────

  private startFallbackPolling() {
    if (this.pollSub) return;
    this.pollSub = interval(this.fallbackPollInterval)
      .pipe(
        switchMap(() =>
          this.http.get<{ unread_count: number }>(`${this.apiUrl}/unread_count/`).pipe(
            catchError(() => of({ unread_count: 0 }))
          )
        )
      )
      .subscribe((res) => this.unreadCount.set(res.unread_count));
  }

  /** @deprecated Use connect(token) instead. Kept for backwards compatibility. */
  startPolling(intervalMs = 30000) {
    this.stopPolling();
    this.fetchUnreadCount();
    this.pollSub = interval(intervalMs)
      .pipe(
        switchMap(() =>
          this.http.get<{ unread_count: number }>(`${this.apiUrl}/unread_count/`).pipe(
            catchError(() => of({ unread_count: 0 }))
          )
        )
      )
      .subscribe((res) => this.unreadCount.set(res.unread_count));
  }

  /** @deprecated Use disconnect() instead. */
  stopPolling() {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  private stopAll() {
    this.wsSub?.unsubscribe();
    this.wsSub = undefined;
    this.ws$?.complete();
    this.ws$ = undefined;
    this.reconnectSub?.unsubscribe();
    this.reconnectSub = undefined;
    this.reconnectAttempts = 0;
    this.stopPolling();
  }

  // ── HTTP API methods (unchanged) ───────────────────────────────────

  fetchUnreadCount() {
    this.http
      .get<{ unread_count: number }>(`${this.apiUrl}/unread_count/`)
      .pipe(catchError(() => of({ unread_count: 0 })))
      .subscribe((res) => this.unreadCount.set(res.unread_count));
  }

  getNotifications(page = 1, isRead?: boolean) {
    let url = `${this.apiUrl}/?page=${page}`;
    if (isRead !== undefined) {
      url += `&is_read=${isRead}`;
    }
    return this.http.get<NotificationPage>(url);
  }

  markAsRead(id: number) {
    return this.http.patch<Notification>(`${this.apiUrl}/${id}/`, { is_read: true });
  }

  markAllRead() {
    return this.http.post<{ marked_read: number }>(`${this.apiUrl}/mark_all_read/`, {});
  }

  getPreferences() {
    return this.http.get<NotificationPreference[]>(`${this.prefUrl}/`);
  }

  updatePreference(id: number, enabled: boolean) {
    return this.http.patch<NotificationPreference>(`${this.prefUrl}/${id}/`, { enabled });
  }
}
